import { ModalContext } from "app/app";
import axios from "axios";
import { FieldInput } from "components/fieldInput";
import { ViewContext } from "components/lib";
import { Form, Formik } from "formik";
import { Fragment, useContext, useEffect, useState } from "react";
import * as Yup from "yup";
import Loader from "./images/loader.gif";
import styles from "./styles.module.scss";

export function UserModal() {
  const context = useContext(ViewContext);
  const { modal, setModal } = useContext(ModalContext);

  const [loading, setLoading] = useState(false);
  const [agencies, setAgencies] = useState(false);
  const [agency, setAgency] = useState(agencies ? agencies[0].value : null);

  const noSpecialCharactersRegex = /^[A-Za-z0-9 ]*$/;

  const permissionOptions = [
    { value: "user", label: "User" },
    { value: "owner", label: "Owner" }
  ];

  const validationSchema = Yup.object().shape({
    first_name: Yup.string()
      .required("This field is required!")
      .matches(
        noSpecialCharactersRegex,
        "First Name must not contain special characters"
      ),
    last_name: Yup.string()
      .required("This field is required!")
      .matches(
        noSpecialCharactersRegex,
        "Last Name must not contain special characters"
      ),
    email: Yup.string().email().required("This field is required!"),
  });

  async function fetchAccountData() {
    if (modal.agency_id || (modal.mode && modal.mode === "edit_mode")) return;

    const tempData = [];

    await axios
      .get("/api/account/list/all")
      .then((res) => {
        res.data.data.map((value) => {
          tempData.push({
            value: value.id,
            label: value.name,
          });
        });

        setAgencies(tempData);
      })
      .catch((err) => {
        console.error("Error: " + err);

        context.notification.show(
          "Failed to fetch agency data!",
          "error",
          false,
          "toast",
          "x-circle"
        );
      });
  }

  useEffect(() => {
    fetchAccountData();
  }, []);

  const handleFormSubmit = async (values) => {
    setLoading(true);
    const first_name = values.first_name.trim();
    const last_name = values.last_name.trim();
    const email = values.email.trim();

    try {
      const res = await axios.get(
        "/api/user/search?page=1&search=" + email
      );

      if (modal.mode && modal.mode === "edit_mode") {
        try {
          await axios.patch("/api/user/" + modal.data.id, {
            first_name: first_name,
            last_name: last_name,
            email: email,
            permission: values.permission,
            account_id:
              modal.data.agency_id || modal.data.agency?.id || undefined,
          });

          setLoading(false);
          setModal("");

          fetchAccountData();

          context.notification.show(
            "User Updated!",
            "success",
            false,
            "toast",
            "check-circle"
          );

          if (modal.action) {
            modal.action({
              id: modal.data.id,
              name: `${first_name} ${last_name}`.trim(),
              email,
              permission: values.permission,
            });
          }
        } catch (error) {
          console.log(error);

          context.notification.show(
            "Failed to update user!",
            "error",
            false,
            "toast",
            "x-circle"
          );

          setLoading(false);
          setModal("");
        }
      } else {
        if (res.data.data.data.length === 0) {
          try {
            await axios.post("/api/invite", {
              first_name: values.first_name,
              last_name: values.last_name,
              email: values.email,
              agency: modal.agency_id ? modal.agency_id : agency,
              type: "user",
              permission: values.permission,
            });

            setLoading(false);
            setModal("");

            fetchAccountData();

            context.notification.show(
              "Invite sent!",
              "success",
              false,
              "toast",
              "check-circle"
            );
          } catch (error) {
            console.log(error);

            context.notification.show(
              "Failed to send invitation!",
              "error",
              false,
              "toast",
              "x-circle"
            );

            setLoading(false);
            setModal("");
          }
        } else {
          setLoading(false);
          setModal("");

          context.notification.show(
            "That email is already registered",
            "error",
            false,
            "toast",
            "check-circle"
          );
        }
      }
    } catch (error) {
      console.log(error);
      setLoading(false);
      setModal("");

      context.notification.show(
        "An error occurred while checking email availability",
        "error",
        false,
        "toast",
        "x-circle"
      );
    }
  };

  return (
    <Fragment>
      {modal.name === "user_modal" && (
        <div className={styles.background}>
          <div className={styles.wrapper}>
            <div className={styles.insideWrapper}>
              <div className={styles.title}>Invite User</div>
              <div className={styles.line} />
              <Formik
                validationSchema={validationSchema}
                initialValues={{
                  first_name:
                    modal.mode && modal.mode === "edit_mode"
                      ? modal.data.first_name
                      : "",
                  last_name:
                    modal.mode && modal.mode === "edit_mode"
                      ? modal.data.last_name
                      : "",
                  email:
                    modal.mode && modal.mode === "edit_mode"
                      ? modal.data.email
                      : "",
                  agency,
                  permission:
                    modal.mode && modal.mode === "edit_mode"
                      ? modal.data.permission || "user"
                      : "user",
                }}
                onSubmit={handleFormSubmit}
              >
                {({
                  errors,
                  touched,
                  handleSubmit,
                  handleChange,
                  handleBlur,
                  submitForm,
                }) => (
                  <Fragment>
                    <Form className={styles.form} onSubmit={handleSubmit}>
                      <div
                        className={styles.fieldRow}
                        style={{ paddingBottom: 0 }}
                      >
                        <FieldInput
                          id="first_name"
                          type="text"
                          label="First Name"
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          required
                          showErrors
                          errors={errors.first_name}
                          touched={touched.first_name}
                        />
                        <FieldInput
                          id="last_name"
                          type="text"
                          label="Last Name"
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          required
                          showErrors
                          errors={errors.last_name}
                          touched={touched.last_name}
                        />
                      </div>
                      <div
                        className={styles.fieldRow}
                        style={{ paddingBottom: 0 }}
                      >
                        <FieldInput
                          id="email"
                          type="text"
                          label="Email"
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          required
                          showErrors
                          errors={errors.email}
                          touched={touched.email}
                        />
                        <FieldInput
                          name="permission"
                          id="permission"
                          type="select"
                          label="Permission Level"
                          handleChange={handleChange}
                          errors={errors.permission}
                          touched={touched.permission}
                          showErrors
                          required
                          options={permissionOptions}
                        />
                      </div>
                      {!modal.agency_id && !modal.mode && (
                        <div
                          className={styles.fieldRow}
                          style={{ paddingBottom: 0 }}
                        >
                          <FieldInput
                            name="agency"
                            type="search_select"
                            label="Agency"
                            defaultValue={agency}
                            handleChange={(event) => {
                              setAgency(event.value);
                            }}
                            errors={errors.agency}
                            touched={touched.agency}
                            showErrors
                            required
                            options={agencies ? agencies : []}
                          />
                        </div>
                      )}
                    </Form>
                    <div
                      className={styles.footer}
                      style={{ justifyContent: "flex-end" }}
                    >
                      <div className={styles.buttons}>
                        <button
                          className={styles.cancel}
                          onClick={() => setModal("")}
                        >
                          Cancel
                        </button>
                        <button className={styles.save} onClick={submitForm}>
                          {loading ? (
                            <img
                              src={Loader}
                              alt="Loader"
                              className={styles.loader}
                            />
                          ) : modal.mode && modal.mode === "edit_mode" ? (
                            "Update"
                          ) : (
                            "Invite"
                          )}
                        </button>
                      </div>
                    </div>
                  </Fragment>
                )}
              </Formik>
            </div>
          </div>
        </div>
      )
      }
    </Fragment >
  );
}
